import { useContext, useEffect } from "react";
import { useForm } from "react-final-form";

import { DateTime } from "@bps/date-time";
import { MessageBar, MessageBarType, Stack, Text } from "@bps/fluent-ui";
import { AppointmentFormValues } from "@shared-types/booking/appointment-form-values.types.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { FieldCondition } from "@ui-components/form/FieldCondition.tsx";
import { Fieldset } from "@ui-components/form/Fieldset/Fieldset.tsx";
import { FieldSpy } from "@ui-components/form/FieldSpy.tsx";

import { appointmentFormNameOf } from "./AppointmentForm.types.ts";
import { AppointmentFormContext } from "./context/AppointmentFormContext.ts";
import { RecurrenceRuleDisplay } from "./RecurrenceRuleDisplay.tsx";

interface RecurringAppointmentConflictProps {
  isDetailedConflictVisible?: boolean;
  hideInitialStateRrule?: boolean;
}

export const RecurringAppointmentConflicts: React.FunctionComponent<
  RecurringAppointmentConflictProps
> = ({ isDetailedConflictVisible = true, hideInitialStateRrule = false }) => {
  const { booking } = useStores();
  const {
    calendarEvent,
    getNextRecurrenceDates,
    loadNextRecurrenceDates,
    reccurrenceHelperText
  } = useContext(AppointmentFormContext);

  const { recurringAppointmentConflicts } = reccurrenceHelperText || {};

  const conflictsText = () => {
    let text = "";
    recurringAppointmentConflicts?.forEach(conflict => {
      const dateFormatted = DateTime.fromISO(
        conflict.conflictDateTime
      ).toDayDefaultFormat();
      text += `${dateFormatted}, `;
    });

    return text.substring(0, text.length - 2);
  };

  const form = useForm<AppointmentFormValues>();
  useEffect(() => {
    getNextRecurrenceDates(form.getState().values);
  }, [form, getNextRecurrenceDates]);

  const onRecurrenceChange = (value: any, values: AppointmentFormValues) =>
    loadNextRecurrenceDates(values);

  const onAppointmentChange = (value: any, values: AppointmentFormValues) => {
    if (!calendarEvent) {
      loadNextRecurrenceDates(values);
    }
  };

  return (
    <Fieldset styles={{ root: { marginTop: 8 } }}>
      <FieldCondition when={appointmentFormNameOf("repeat")} is={true}>
        {!booking.ui.isEditSingleEvent &&
          recurringAppointmentConflicts &&
          recurringAppointmentConflicts?.length > 0 && (
            <Stack>
              <MessageBar
                messageBarType={MessageBarType.warning}
                styles={{
                  root: {
                    maxHeight: 450,
                    overflowY: "auto"
                  }
                }}
              >
                {isDetailedConflictVisible &&
                  recurringAppointmentConflicts.length === 1 && (
                    <Text>{`There is a conflict on the ${DateTime.fromISO(
                      recurringAppointmentConflicts[0].conflictDateTime
                    ).toDayDefaultFormat()}`}</Text>
                  )}

                {isDetailedConflictVisible &&
                  recurringAppointmentConflicts.length !== 1 && (
                    <Stack.Item>
                      <Text>There are conflicts on multiple dates:</Text>
                      <Stack.Item>
                        <Text>{conflictsText()}</Text>
                      </Stack.Item>
                    </Stack.Item>
                  )}

                {!isDetailedConflictVisible && (
                  <Text>There are conflicts in this series</Text>
                )}
              </MessageBar>
            </Stack>
          )}

        <FieldSpy
          name={appointmentFormNameOf("duration")}
          onChange={onRecurrenceChange}
        />
        <FieldSpy
          name={appointmentFormNameOf("providerId")}
          onChange={onRecurrenceChange}
        />
        <FieldSpy
          name={appointmentFormNameOf("startDate")}
          onChange={onAppointmentChange}
        />
        <FieldSpy
          name={appointmentFormNameOf("startTime")}
          onChange={onAppointmentChange}
        />
        <FieldSpy
          name={appointmentFormNameOf("repeat")}
          onChange={onRecurrenceChange}
        />
        <FieldSpy
          name={appointmentFormNameOf("interval")}
          onChange={onRecurrenceChange}
        />
        <FieldSpy
          name={appointmentFormNameOf("dayRecur")}
          onChange={onRecurrenceChange}
        />
        <FieldSpy
          name={appointmentFormNameOf("monthDayRecur")}
          onChange={onRecurrenceChange}
        />
        <FieldSpy
          name={appointmentFormNameOf("endScheduleType")}
          onChange={onRecurrenceChange}
        />
        <FieldSpy
          name={appointmentFormNameOf("frequency")}
          onChange={onRecurrenceChange}
        />
        <FieldSpy
          name={appointmentFormNameOf("until")}
          onChange={onRecurrenceChange}
        />
        <FieldSpy
          name={appointmentFormNameOf("count")}
          onChange={onRecurrenceChange}
        />

        <RecurrenceRuleDisplay hideInitialState={hideInitialStateRrule} />
      </FieldCondition>
    </Fieldset>
  );
};
